import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import config from '@/config';

const leadSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters." }),
  business_email: z.string().email({ message: "Invalid email address." }),
  intent_category: z.string({ required_error: "Please select a category." }).min(1, "Please select a category."),
  company: z.string().optional(),
  bottleneck: z.string().optional(),
});

/**
 * Get the appropriate API endpoint for form submission
 * Uses Lambda endpoint if configured, otherwise falls back to FastAPI backend
 */
const getApiEndpoint = () => {
  if (config.features.useLambdaContactForm && config.api.contactFormEndpoint) {
    return config.api.contactFormEndpoint;
  }
  // Fallback to existing backend
  const backendUrl = config.api.backendBaseUrl || '';
  return `${backendUrl}/api/leads`;
};

export default function LeadForm({ defaultIntent, hideIntent, isContactPage = false }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);

  // Initialize reCAPTCHA Enterprise - dynamically load script with site key
  useEffect(() => {
    if (!config.recaptcha.enabled || !config.recaptcha.siteKey) {
      return;
    }

    // Check if script already loaded
    const existingScript = document.querySelector('script[src*="recaptcha/enterprise.js"]');
    if (existingScript) {
      // Script exists, just wait for it to be ready
      const waitForRecaptcha = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise && window.grecaptcha.enterprise.ready) {
          window.grecaptcha.enterprise.ready(() => setRecaptchaReady(true));
        } else {
          setTimeout(waitForRecaptcha, 100);
        }
      };
      waitForRecaptcha();
      return;
    }

    // Dynamically load reCAPTCHA Enterprise script with site key
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${config.recaptcha.siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const waitForReady = () => {
        if (window.grecaptcha && window.grecaptcha.enterprise && window.grecaptcha.enterprise.ready) {
          window.grecaptcha.enterprise.ready(() => setRecaptchaReady(true));
        } else {
          setTimeout(waitForReady, 100);
        }
      };
      waitForReady();
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup on unmount (optional)
    };
  }, []);

  /**
   * Get reCAPTCHA Enterprise token for form submission
   * Returns null if reCAPTCHA is disabled or not ready
   */
  const getRecaptchaToken = async () => {
    if (!config.recaptcha.enabled || !config.recaptcha.siteKey || !recaptchaReady) {
      return null;
    }

    try {
      const token = await window.grecaptcha.enterprise.execute(config.recaptcha.siteKey, { action: 'contact_form' });
      return token;
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      return null;
    }
  };

  const form = useForm({
    resolver: zodResolver(leadSchema),
    defaultValues: {
      name: "",
      business_email: "",
      intent_category: defaultIntent || "",
      company: "",
      bottleneck: "",
    },
  });

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    // Auto-detect timezone
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get reCAPTCHA token if enabled
    const recaptchaToken = await getRecaptchaToken();

    const payload = {
      ...values,
      user_timezone: userTimezone,
      ...(recaptchaToken && { recaptcha_token: recaptchaToken }),
    };

    try {
      const endpoint = getApiEndpoint();
      await axios.post(endpoint, payload);
      track('lead_form_submitted', { intent_category: values.intent_category || '-', page: window.location.pathname });
      toast.success("Request received.", {
        description: "We will be in touch shortly.",
      });
      form.reset();
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed.", {
        description: "Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className={`bg-background text-foreground ${isContactPage ? 'border-none shadow-none' : 'border-4 border-primary shadow-block'}`} data-testid="lead-form-card">
      {!isContactPage && (
        <CardHeader>
          <CardTitle className="text-3xl" data-testid="lead-form-title">Get In Touch</CardTitle>
          <CardDescription data-testid="lead-form-desc">Ready to elevate your digital infrastructure?</CardDescription>
        </CardHeader>
      )}
      <CardContent className={isContactPage ? "p-0" : ""}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" data-testid="lead-form">
          <div className="space-y-2">
            <label htmlFor="name" className="font-heading font-bold text-sm uppercase">Name</label>
            <Input
              id="name"
              placeholder="Your Name"
              {...form.register("name")}
              className={form.formState.errors.name ? "border-destructive" : ""}
              data-testid="lead-form-name"
            />
            {form.formState.errors.name && (
              <p className="text-destructive text-xs" data-testid="lead-form-error-name">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <label htmlFor="email" className="font-heading font-bold text-sm uppercase">Business Email</label>
            <Input
              id="email"
              placeholder="name@company.com"
              {...form.register("business_email")}
              className={form.formState.errors.business_email ? "border-destructive" : ""}
              data-testid="lead-form-email"
            />
            {form.formState.errors.business_email && (
              <p className="text-destructive text-xs" data-testid="lead-form-error-email">{form.formState.errors.business_email.message}</p>
            )}
          </div>

          {isContactPage && (
            <div className="space-y-2">
              <label htmlFor="company" className="font-heading font-bold text-sm uppercase">Company</label>
              <Input
                id="company"
                placeholder="Organization Name"
                {...form.register("company")}
                data-testid="lead-form-company"
              />
            </div>
          )}

          {hideIntent ? (
            <input type="hidden" {...form.register("intent_category")} value={defaultIntent} />
          ) : (
            <div className="space-y-2">
              <label htmlFor="intent" className="font-heading font-bold text-sm uppercase">Interest</label>
              <Select onValueChange={(val) => form.setValue("intent_category", val)} defaultValue={defaultIntent || form.getValues("intent_category")}>
                <SelectTrigger id="intent" className={form.formState.errors.intent_category ? "border-destructive" : ""} data-testid="lead-form-select">
                  <SelectValue placeholder="Select a Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Enterprise Modernisation Audit" data-testid="select-item-enterprise">Enterprise Modernisation Audit</SelectItem>
                  <SelectItem value="Venture Product Engineering" data-testid="select-item-venture">Venture Product Engineering</SelectItem>
                  <SelectItem value="Design" data-testid="select-item-design">Design</SelectItem>
                  <SelectItem value="Matrix Download">Build vs Buy Matrix</SelectItem>
                </SelectContent>
              </Select>
              {form.formState.errors.intent_category && (
                <p className="text-destructive text-xs" data-testid="lead-form-error-intent">{form.formState.errors.intent_category.message}</p>
              )}
            </div>
          )}

          {isContactPage && (
            <div className="space-y-2">
              <label htmlFor="bottleneck" className="font-heading font-bold text-sm uppercase">The Bottleneck</label>
              <Textarea
                id="bottleneck"
                placeholder="Briefly describe your challenge..."
                {...form.register("bottleneck")}
                data-testid="lead-form-bottleneck"
              />
            </div>
          )}

          <Button type="submit" className="w-full mt-4" disabled={isSubmitting} data-testid="lead-form-submit">
            {isSubmitting ? "Submitting..." : "Submit Request"}
          </Button>

          {/* reCAPTCHA disclaimer - required when hiding badge */}
          {/* Source - https://stackoverflow.com/a */}
          {/* Posted by Yann39, modified by community. See post 'Timeline' for change history */}
          {/* Retrieved 2026-01-14, License - CC BY-SA 4.0 */}
          <p className="text-xs text-muted-foreground mt-4 text-center">
            This site is protected by reCAPTCHA and the Google{' '}
            <a href="https://policies.google.com/privacy" className="underline hover:text-primary" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="https://policies.google.com/terms" className="underline hover:text-primary" target="_blank" rel="noopener noreferrer">Terms of Service</a> apply.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
